import React, { useState } from 'react';
import axios from 'axios';
import { useT } from '../i18n';
import { useLang } from '../context/LangContext';

export default function ResetPassword() {
  const t = useT();
  const { lang, setLang } = useLang();

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  const [password,  setPassword]  = useState('');
  const [password2, setPassword2] = useState('');
  const [loading,   setLoading]   = useState(false);
  const [done,      setDone]      = useState(false);
  const [error,     setError]     = useState(null);

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid rgba(59,73,75,0.6)',
    color: '#e2e2e8',
    fontFamily: "var(--font-display)",
    fontSize: 15,
    padding: '8px 0',
    outline: 'none',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== password2) {
      setError(t('nav_err_pw_match'));
      return;
    }
    if (password.length < 10) {
      setError(t('nav_err_pw_short'));
      return;
    }
    setLoading(true);
    try {
      await axios.post('/api/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || t('reset_error_generic'));
    } finally {
      setLoading(false);
    }
  };

  // No token in URL — show error
  if (!token) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
        <div style={{ maxWidth: 420, width: '100%', background: 'rgba(17,19,24,0.95)', border: '1px solid rgba(59,73,75,0.35)', padding: '40px 36px', textAlign: 'center' }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', color: 'var(--brand-primary)', marginBottom: 32 }}>CRIT ROSTER</div>
          <div style={{ fontSize: 36, marginBottom: 20 }}>✕</div>
          <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 800, color: '#e2e2e8' }}>{t('reset_invalid_title')}</h2>
          <p style={{ margin: '0 0 28px', fontSize: 13, color: 'rgba(185,202,203,0.7)', lineHeight: 1.6 }}>{t('reset_invalid_text')}</p>
          <a href="/forgot-password" style={{ display: 'block', width: '100%', boxSizing: 'border-box', padding: '14px 20px', background: 'var(--brand-primary)', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textDecoration: 'none', textAlign: 'center' }}>
            {t('reset_request_new')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--surface-base)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
    }}>
      {/* Lang toggle */}
      <div style={{ position: 'fixed', top: 20, right: 20, display: 'flex', gap: 6 }}>
        {['de', 'en'].map(l => (
          <button key={l} onClick={() => setLang(l)} style={{
            background: 'transparent',
            border: `1px solid ${lang === l ? 'rgba(var(--brand-primary-rgb),0.6)' : 'rgba(59,73,75,0.35)'}`,
            color: lang === l ? 'var(--brand-primary)' : '#849495',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.12em',
            padding: '5px 10px',
            cursor: 'pointer',
          }}>{l.toUpperCase()}</button>
        ))}
      </div>

      <div style={{
        width: '100%',
        maxWidth: 420,
        background: 'rgba(17,19,24,0.95)',
        border: '1px solid rgba(59,73,75,0.35)',
        padding: '40px 36px',
      }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.2em',
          color: 'var(--brand-primary)',
          marginBottom: 32,
        }}>
          CRIT ROSTER
        </div>

        {done ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 20 }}>✓</div>
            <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 800, color: '#e2e2e8' }}>
              {t('reset_success_title')}
            </h2>
            <p style={{ margin: '0 0 28px', fontSize: 13, color: 'rgba(185,202,203,0.7)', lineHeight: 1.6 }}>
              {t('reset_success_text')}
            </p>
            <a href="/" style={{
              display: 'block',
              width: '100%',
              boxSizing: 'border-box',
              padding: '14px 20px',
              background: 'var(--brand-primary)',
              color: '#002022',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textDecoration: 'none',
              textAlign: 'center',
            }}>
              {t('verify_go_login')}
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: '#e2e2e8' }}>
              {t('reset_title')}
            </h2>
            <p style={{ margin: '0 0 28px', fontSize: 13, color: 'rgba(185,202,203,0.7)', lineHeight: 1.6 }}>
              {t('reset_subtitle')}
            </p>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.13em', color: '#849495' }}>
                {t('reset_new_pw')}
              </label>
              <input
                type="password"
                value={password}
                required
                autoFocus
                onChange={e => setPassword(e.target.value)}
                style={inputStyle}
                onFocus={e => e.target.style.borderBottomColor = 'var(--brand-primary)'}
                onBlur={e  => e.target.style.borderBottomColor = 'rgba(59,73,75,0.6)'}
              />
            </div>

            <div style={{ marginBottom: 28 }}>
              <label style={{ display: 'block', marginBottom: 8, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.13em', color: '#849495' }}>
                {t('register_pw_confirm')}
              </label>
              <input
                type="password"
                value={password2}
                required
                onChange={e => setPassword2(e.target.value)}
                style={inputStyle}
                onFocus={e => e.target.style.borderBottomColor = 'var(--brand-primary)'}
                onBlur={e  => e.target.style.borderBottomColor = 'rgba(59,73,75,0.6)'}
              />
              <div style={{ marginTop: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: '#849495', letterSpacing: '0.08em' }}>
                {t('register_pw_min')}
              </div>
            </div>

            {error && (
              <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(192,0,43,0.12)', border: '1px solid rgba(192,0,43,0.35)', color: '#ff6b6b', fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '16px 20px',
                background: loading ? 'rgba(var(--brand-primary-rgb),0.4)' : 'var(--brand-primary)',
                border: 'none',
                color: '#002022',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.14em',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? '...' : t('reset_save')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
